import React, { useMemo } from "react";
import { Grid } from "@mui/material";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 3 PRO React examples
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";

function AsetStatistics({ asetData }) {
  // Hitung statistik dari data aset
  const statistics = useMemo(() => {
    if (!asetData || asetData.length === 0) {
      return {
        totalAset: 0,
        totalNilai: 0,
        totalBarangBergerak: 0,
        totalBarangTidakBergerak: 0,
        nilaiBarangBergerak: 0,
        nilaiBarangTidakBergerak: 0,
        asetPerKeadaan: {},
        asetPerKecamatan: {},
        asetPerTahun: {},
      };
    }

    let totalAset = 0;
    let totalNilai = 0;
    let totalBarangBergerak = 0;
    let totalBarangTidakBergerak = 0;
    let nilaiBarangBergerak = 0;
    let nilaiBarangTidakBergerak = 0;
    const asetPerKeadaan = {};
    const asetPerKecamatan = {};
    const asetPerTahun = {};

    asetData.forEach((aset, index) => {
      const jumlah = parseInt(aset.jumlah_barang) || 0;
      const harga = parseInt(aset.harga_satuan) || 0;
      const nilai = jumlah * harga;

      totalAset += jumlah;
      totalNilai += nilai;

      // Hitung berdasarkan jenis barang
      if (aset.jenis_barang === "Barang Bergerak") {
        totalBarangBergerak += jumlah;
        nilaiBarangBergerak += nilai;
      } else if (aset.jenis_barang === "Barang Tidak Bergerak") {
        totalBarangTidakBergerak += jumlah;
        nilaiBarangTidakBergerak += nilai;
      }

      // Hitung berdasarkan keadaan barang
      const keadaan = aset.keadaan_barang || "Tidak Diketahui";
      if (!asetPerKeadaan[keadaan]) {
        asetPerKeadaan[keadaan] = { jumlah: 0, nilai: 0 };
      }
      asetPerKeadaan[keadaan].jumlah += jumlah;
      asetPerKeadaan[keadaan].nilai += nilai;

      // Hitung berdasarkan kecamatan
      const kecamatan = aset.lokasi || "Tidak Diketahui";
      if (!asetPerKecamatan[kecamatan]) {
        asetPerKecamatan[kecamatan] = { jumlah: 0, nilai: 0 };
      }
      asetPerKecamatan[kecamatan].jumlah += jumlah;
      asetPerKecamatan[kecamatan].nilai += nilai;

      // Hitung berdasarkan tahun perolehan
      const tahun = aset.tahun_perolehan || "Tidak Diketahui";
      if (!asetPerTahun[tahun]) {
        asetPerTahun[tahun] = { jumlah: 0, nilai: 0 };
      }
      asetPerTahun[tahun].jumlah += jumlah;
      asetPerTahun[tahun].nilai += nilai;
    });

    const result = {
      totalAset,
      totalNilai,
      totalBarangBergerak,
      totalBarangTidakBergerak,
      nilaiBarangBergerak,
      nilaiBarangTidakBergerak,
      asetPerKeadaan,
      asetPerKecamatan,
      asetPerTahun,
    };

    return result;
  }, [asetData]);

  // Data untuk grafik tahunan (5 tahun ke belakang)
  const tahunanChartData = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    const values = [];

    for (let i = 4; i >= 0; i--) {
      const year = currentYear - i;
      years.push(year.toString());
      const yearData = statistics.asetPerTahun[year];
      values.push(yearData ? yearData.nilai : 0);
    }

    // Jika semua nilai 0, gunakan data dummy untuk testing
    const hasData = values.some((value) => value > 0);
    const finalValues = hasData ? values : [0, 0, 0, 0, 0];

    const chartData = {
      labels: years,
      datasets: {
        label: "Total Nilai Aset (Rp)",
        data: finalValues,
      },
    };

    return chartData;
  }, [statistics.asetPerTahun]);

  // Data untuk grafik jenis barang
  const jenisBarangChartData = useMemo(() => {
    // Jika data kosong, gunakan data dummy untuk testing
    const bergerak =
      statistics.totalBarangBergerak > 0 ? statistics.totalBarangBergerak : 0;
    const tidakBergerak =
      statistics.totalBarangTidakBergerak > 0
        ? statistics.totalBarangTidakBergerak
        : 0;

    const chartData = {
      labels: ["Barang Bergerak", "Barang Tidak Bergerak"],
      datasets: {
        label: "Jumlah Barang",
        data: [bergerak, tidakBergerak],
      },
    };

    return chartData;
  }, [statistics]);

  // Data untuk grafik kecamatan
  const kecamatanChartData = useMemo(() => {
    const labels = Object.keys(statistics.asetPerKecamatan);
    const values = labels.map(
      (kecamatan) => statistics.asetPerKecamatan[kecamatan].nilai
    );

    return {
      labels,
      datasets: [
        {
          label: "Total Nilai Aset (Rp)",
          color: "primary",
          data: values,
        },
      ],
    };
  }, [statistics.asetPerKecamatan]);

  // Data untuk grafik keadaan barang
  const keadaanChartData = useMemo(() => {
    const labels = Object.keys(statistics.asetPerKeadaan);
    const values = labels.map(
      (keadaan) => statistics.asetPerKeadaan[keadaan].nilai
    );

    return {
      labels,
      datasets: [
        {
          label: "Total Nilai Aset (Rp)",
          color: "secondary",
          data: values,
        },
      ],
    };
  }, [statistics.asetPerKeadaan]);

  // Format currency
  const formatCurrency = (value) => {
    if (value >= 1000000000) {
      return `Rp ${(value / 1000000000).toFixed(2)} M`;
    } else if (value >= 1000000) {
      return `Rp ${(value / 1000000).toFixed(2)} Jt`;
    } else if (value >= 1000) {
      return `Rp ${(value / 1000).toFixed(2)} Rb`;
    }
    return `Rp ${value.toLocaleString("id-ID")}`;
  };

  return (
    <MDBox mb={3}>
      {/* Statistik Cards */}
      <MDBox mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} lg={3}>
            <ComplexStatisticsCard
              icon="inventory"
              title="Total Aset"
              count={statistics.totalAset.toLocaleString("id-ID")}
              percentage={{
                color: "success",
                amount: `${asetData?.length || 0}`,
                label: "jenis barang",
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <ComplexStatisticsCard
              icon="account_balance_wallet"
              title="Total Nilai"
              count={formatCurrency(statistics.totalNilai)}
              percentage={{
                color: "info",
                amount: "Aset",
                label: "seluruh inventaris",
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <ComplexStatisticsCard
              icon="directions_car"
              title="Barang Bergerak"
              count={statistics.totalBarangBergerak.toLocaleString("id-ID")}
              percentage={{
                color: "warning",
                amount: formatCurrency(statistics.nilaiBarangBergerak),
                label: "total nilai",
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <ComplexStatisticsCard
              icon="home"
              title="Barang Tidak Bergerak"
              count={statistics.totalBarangTidakBergerak.toLocaleString(
                "id-ID"
              )}
              percentage={{
                color: "error",
                amount: formatCurrency(statistics.nilaiBarangTidakBergerak),
                label: "total nilai",
              }}
            />
          </Grid>
        </Grid>
      </MDBox>

      {/* Grafik Laporan Tahunan */}
      <MDBox mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <ReportsLineChart
              color="info"
              title="Laporan Tahunan Aset"
              description="Total nilai aset berdasarkan tahun perolehan (5 tahun terakhir)"
              date="Data real-time"
              chart={tahunanChartData}
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <ReportsBarChart
              color="success"
              title="Jenis Barang"
              description="Perbandingan barang bergerak vs tidak bergerak"
              date="Berdasarkan jenis barang"
              chart={jenisBarangChartData}
            />
          </Grid>
        </Grid>
      </MDBox>

      {/* Grafik Kecamatan dan Keadaan */}
      <MDBox mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <HorizontalBarChart
              title="Aset per Kecamatan"
              description="Total nilai aset berdasarkan lokasi"
              chart={kecamatanChartData}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <HorizontalBarChart
              title="Aset per Keadaan"
              description="Total nilai aset berdasarkan kondisi barang"
              chart={keadaanChartData}
            />
          </Grid>
        </Grid>
      </MDBox>

      {/* Ringkasan Detail */}
      <MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <MDBox
              component="div"
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              p={3}
              mb={2}
            >
              <MDTypography variant="h6" color="white" fontWeight="medium">
                Ringkasan Kecamatan
              </MDTypography>
              <MDBox mt={2}>
                {Object.entries(statistics.asetPerKecamatan).map(
                  ([kecamatan, data]) => (
                    <MDBox
                      key={kecamatan}
                      display="flex"
                      justifyContent="space-between"
                      mb={1}
                    >
                      <MDTypography variant="body2" color="white">
                        {kecamatan}
                      </MDTypography>
                      <MDTypography
                        variant="body2"
                        color="white"
                        fontWeight="medium"
                      >
                        {formatCurrency(data.nilai)}
                      </MDTypography>
                    </MDBox>
                  )
                )}
              </MDBox>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6}>
            <MDBox
              component="div"
              variant="gradient"
              bgColor="success"
              borderRadius="lg"
              coloredShadow="success"
              p={3}
              mb={2}
            >
              <MDTypography variant="h6" color="white" fontWeight="medium">
                Ringkasan Keadaan
              </MDTypography>
              <MDBox mt={2}>
                {Object.entries(statistics.asetPerKeadaan).map(
                  ([keadaan, data]) => (
                    <MDBox
                      key={keadaan}
                      display="flex"
                      justifyContent="space-between"
                      mb={1}
                    >
                      <MDTypography variant="body2" color="white">
                        {keadaan}
                      </MDTypography>
                      <MDTypography
                        variant="body2"
                        color="white"
                        fontWeight="medium"
                      >
                        {formatCurrency(data.nilai)}
                      </MDTypography>
                    </MDBox>
                  )
                )}
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    </MDBox>
  );
}

export default AsetStatistics;
